import {
  AlertTriangle,
  ArrowUpDown,
  Check,
  ChevronRight,
  Copy,
  Layers,
  Link2,
  Minus,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { HistoryButton } from '@/features/audit-log';
import { useCountryScope, usePermissions } from '@/features/auth';
import { MappingsPanel } from '@/features/providers';
import { SortHeaderButton } from '@/shared/components/data-table/data-table-column-header';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { EmptyState } from '@/shared/components/empty-state';
import { SearchInput } from '@/shared/components/search-input';
import { StatusBadge } from '@/shared/components/status-badge';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';
import { formatCurrency } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

import { CatalogueChangeDialog } from './catalogue-change-dialog';
import { DeleteDenominationDialog } from './delete-catalogue-dialogs';
import { DenominationFormDialog } from './denomination-form-dialog';
import { DenominationOrderDialog } from './denomination-order-dialog';
import { reasonLabel } from '@/shared/lib/catalogue-labels';
import { describeUsage } from '../lib/catalogue-usage';
import {
  useDeletionCheck,
  useSetAvailability,
  useSetDenominationStatus,
} from '../api/brands.queries';
import type {
  BrandDenomination,
  BrandRegion,
  BrandDetails,
  CatalogueDeletionCheck,
  CatalogueCountry,
  CatalogueImpactRequest,
  DenominationCountryState,
} from '../types/brand.types';
import { DENOMINATION_SORT_DEFAULTS } from '../types/brand.types';

const ANY = 'all';

type BulkChange = 'activate' | 'deactivate' | 'offer' | 'withdraw';

/** A change waiting for confirmation: for the selection, or one row's status. */
interface PendingChange {
  change: BulkChange;
  denominationIds: number[];
  /** Made from the bulk toolbar, so the selection is cleared once it is made. */
  fromSelection: boolean;
  /** Names what changes in the dialog title: the denomination, or how many. */
  noun: string;
}

/**
 * Every denomination of the brand, grouped by region, with its state in the
 * chosen country: offered, visible to customers, purchasable — and, when not,
 * the reasons, all decided by the Admin API.
 *
 * The whole brand is loaded at once (a brand has tens of denominations, not
 * thousands), so the filters here narrow an in-memory list; they still live in
 * the URL so the view can be shared.
 *
 * Bulk actions: activate/deactivate apply everywhere (catalogue.manage and
 * all-country scope); offer/withdraw apply to the chosen country only
 * (catalogue.availability.manage). The same staff can activate or deactivate
 * one denomination from its row. Each is previewed before it is made.
 *
 * Creating and editing a denomination (catalogue.manage and all-country
 * scope) opens its form; status and availability are not part of it. The same
 * staff can delete a denomination nothing uses yet (no order, cart, payment or
 * customer record); a used one shows what uses it instead.
 */
export function BrandDenominations({
  details,
  country,
  countryName,
}: {
  details: BrandDetails;
  /** The country whose states are shown and where offer/withdraw apply. */
  country: CatalogueCountry;
  countryName: (countryId: number) => string;
}) {
  // Same URL sort state as the page, which asks the Admin API for this order.
  const tableState = useTableSearchParams(DENOMINATION_SORT_DEFAULTS);
  const sortedBy = (column: 'name' | 'sortOrder') =>
    tableState.sort === column ? tableState.direction : (false as const);
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [editing, setEditing] = useState<BrandDenomination | 'new' | null>(null);
  const [duplicating, setDuplicating] = useState<BrandDenomination | null>(null);
  const [providersFor, setProvidersFor] = useState<BrandDenomination | null>(null);
  const [deleting, setDeleting] = useState<BrandDenomination | null>(null);
  const [reordering, setReordering] = useState<BrandRegion | null>(null);
  // Regions folded away to look at the others; view-only, so not in the URL.
  const [collapsed, setCollapsed] = useState<ReadonlySet<number>>(() => new Set());

  const setStatus = useSetDenominationStatus();
  const setAvailability = useSetAvailability();

  const canChangeStatus = can('catalogue.manage') && allCountries;
  const canChangeAvailability = can('catalogue.availability.manage');
  const canSeeCost = can('catalogue.cost.read');
  const canSelect = canChangeStatus || canChangeAvailability;
  const deletionCheck = useDeletionCheck(details.brand.id, canChangeStatus);

  const regionFilter = tableState.getFilter('region');
  const statusFilter = tableState.getFilter('status');
  const stateFilter = tableState.getFilter('state');
  const search = tableState.search.trim().toLowerCase();

  const rows = useMemo(
    () =>
      details.denominations.filter((denomination) => {
        const state = stateIn(denomination, country.id);
        if (regionFilter && String(denomination.regionId) !== regionFilter) return false;
        if (statusFilter && denomination.isActive !== (statusFilter === 'active')) return false;
        if (stateFilter === 'sellable' && !state?.sellable) return false;
        if (stateFilter === 'notSellable' && (!state?.offered || state.sellable)) return false;
        if (stateFilter === 'notOffered' && state?.offered) return false;
        if (search && !denomination.name.toLowerCase().includes(search)) return false;
        return true;
      }),
    [details.denominations, country.id, regionFilter, statusFilter, stateFilter, search],
  );

  const groups = useMemo(
    () =>
      details.regions
        .map((region) => ({
          region,
          denominations: rows.filter((denomination) => denomination.regionId === region.id),
        }))
        .filter((group) => group.denominations.length > 0),
    [details.regions, rows],
  );

  // Selection is limited to what is on screen, so a filter change or a
  // collapsed region never leaves hidden rows selected for a bulk action.
  const shownRows = rows.filter((row) => !collapsed.has(row.regionId));
  const visibleSelected = shownRows.filter((row) => selected.has(row.id)).map((row) => row.id);
  const allVisibleSelected = shownRows.length > 0 && visibleSelected.length === shownRows.length;

  const toggleRegion = (regionId: number) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
  const allCollapsed = groups.length > 0 && groups.every(({ region }) => collapsed.has(region.id));
  const setAllCollapsed = (on: boolean) =>
    setCollapsed(on ? new Set(groups.map(({ region }) => region.id)) : new Set());

  const toggle = (id: number, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const toggleAll = (on: boolean) =>
    setSelected(on ? new Set(shownRows.map((row) => row.id)) : new Set());

  const denominationName = (id: number) =>
    details.denominations.find((denomination) => denomination.id === id)?.name ?? `#${id}`;

  const here = countryName(country.id);
  const count = visibleSelected.length;
  const noun = `${count} denomination${count === 1 ? '' : 's'}`;

  const changeSelected = (change: BulkChange) =>
    setPending({ change, denominationIds: visibleSelected, fromSelection: true, noun });
  const changeStatusOf = (denomination: BrandDenomination) =>
    setPending({
      change: denomination.isActive ? 'deactivate' : 'activate',
      denominationIds: [denomination.id],
      fromSelection: false,
      noun: denomination.name,
    });

  const request: CatalogueImpactRequest | null = pending
    ? {
        change: CHANGE[pending.change],
        denominationIds: pending.denominationIds,
        countryId:
          pending.change === 'offer' || pending.change === 'withdraw' ? country.id : undefined,
      }
    : null;

  const onConfirm = (reason: string | null) => {
    if (!pending) return;
    const { change, denominationIds, fromSelection } = pending;
    const done = {
      onSuccess: () => {
        setPending(null);
        if (fromSelection) setSelected(new Set());
      },
    };
    const brandId = details.brand.id;
    if (change === 'activate' || change === 'deactivate') {
      setStatus.mutate({ brandId, denominationIds, isActive: change === 'activate', reason }, done);
    } else {
      setAvailability.mutate(
        {
          brandId,
          countryId: country.id,
          denominationIds,
          offered: change === 'offer',
          reason,
        },
        done,
      );
    }
  };

  return (
    <section aria-labelledby="denominations-heading" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="denominations-heading" className="text-sm font-semibold">
          Denominations
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-muted-foreground text-xs">
            States shown for <strong className="text-foreground">{here}</strong>. Change the country
            in the header.
          </p>
          {groups.length > 1 && (
            <Button size="sm" variant="ghost" onClick={() => setAllCollapsed(!allCollapsed)}>
              {allCollapsed ? 'Expand all regions' : 'Collapse all regions'}
            </Button>
          )}
          {canChangeStatus && (
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden />
              Add denomination
            </Button>
          )}
        </div>
      </div>

      <DataTableToolbar
        hasActiveFilters={tableState.hasActiveFilters}
        onResetFilters={tableState.resetFilters}
      >
        <SearchInput
          value={tableState.search}
          onChange={tableState.setSearch}
          placeholder="Search denominations…"
          className="w-full sm:w-52"
        />
        <FilterSelect
          label="Filter by region"
          value={regionFilter}
          onChange={(value) => tableState.setFilter('region', value)}
          anyLabel="Any region"
          options={details.regions.map((region) => ({
            value: String(region.id),
            label: region.name ?? region.code ?? `#${region.id}`,
          }))}
        />
        <FilterSelect
          label="Filter by status"
          value={statusFilter}
          onChange={(value) => tableState.setFilter('status', value)}
          anyLabel="Any status"
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
        <FilterSelect
          label={`Filter by state in ${here}`}
          value={stateFilter}
          onChange={(value) => tableState.setFilter('state', value)}
          anyLabel={`Any state in ${here}`}
          options={[
            { value: 'sellable', label: 'Purchasable' },
            { value: 'notSellable', label: 'Offered, not purchasable' },
            { value: 'notOffered', label: 'Not offered' },
          ]}
        />
      </DataTableToolbar>

      {canSelect && count > 0 && (
        <div
          role="toolbar"
          aria-label="Bulk actions"
          className="bg-muted/50 border-border flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <span className="font-medium">{noun} selected</span>
          <span className="flex-1" />
          {canChangeAvailability && (
            <>
              <Button size="sm" variant="outline" onClick={() => changeSelected('offer')}>
                Offer in {here}
              </Button>
              <Button size="sm" variant="outline" onClick={() => changeSelected('withdraw')}>
                Withdraw from {here}
              </Button>
            </>
          )}
          {canChangeStatus && (
            <>
              <Button size="sm" variant="outline" onClick={() => changeSelected('activate')}>
                Activate
              </Button>
              <Button size="sm" variant="destructive" onClick={() => changeSelected('deactivate')}>
                Deactivate
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </div>
      )}

      {details.denominations.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No denominations yet"
          description="This brand has no denominations, so there is nothing customers can buy."
          action={
            canChangeStatus ? (
              <Button size="sm" onClick={() => setEditing('new')}>
                <Plus className="size-4" aria-hidden />
                Add denomination
              </Button>
            ) : undefined
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No denominations match your filters"
          description="Clear the filters to see every denomination of this brand."
          action={
            <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {details.countries.length > 1 && <ElsewhereLegend />}
          <div className="border-border overflow-x-auto rounded-md border">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  {canSelect && (
                    <TableHead className="w-8">
                      <Checkbox
                        aria-label="Select all shown denominations"
                        checked={allVisibleSelected}
                        disabled={shownRows.length === 0}
                        onCheckedChange={(value) => toggleAll(value === true)}
                      />
                    </TableHead>
                  )}
                  <TableHead>
                    <SortHeaderButton
                      title="Denomination"
                      sorted={sortedBy('name')}
                      onToggle={() => tableState.toggleSort('name')}
                    />
                  </TableHead>
                  <TableHead className="w-28 text-end">
                    <SortHeaderButton
                      title="Storefront position"
                      sorted={sortedBy('sortOrder')}
                      onToggle={() => tableState.toggleSort('sortOrder')}
                      align="end"
                    />
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Offered</TableHead>
                  <TableHead>Customers</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>In other countries</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map(({ region, denominations }) => (
                  <RegionGroup
                    key={region.id}
                    title={region.name ?? region.code ?? `Region #${region.id}`}
                    regionInactive={!region.isActive}
                    count={denominations.length}
                    expanded={!collapsed.has(region.id)}
                    onToggle={() => toggleRegion(region.id)}
                    columns={8 + (canSelect ? 1 : 0)}
                    onReorder={
                      canChangeStatus && region.denominationCount > 1
                        ? () => setReordering(region)
                        : undefined
                    }
                  >
                    {denominations.map((denomination) => (
                      <DenominationRow
                        key={denomination.id}
                        denomination={denomination}
                        countryId={country.id}
                        otherCountries={details.countries.filter((c) => c.id !== country.id)}
                        canSeeCost={canSeeCost}
                        selectable={canSelect}
                        selected={selected.has(denomination.id)}
                        onSelect={(on) => toggle(denomination.id, on)}
                        onEdit={canChangeStatus ? () => setEditing(denomination) : undefined}
                        onChangeStatus={
                          canChangeStatus ? () => changeStatusOf(denomination) : undefined
                        }
                        onDuplicate={
                          canChangeStatus && details.regions.length > 1
                            ? () => setDuplicating(denomination)
                            : undefined
                        }
                        onManageProviders={() => setProvidersFor(denomination)}
                        deletion={deletionOf(deletionCheck.data, denomination.id)}
                        onDelete={canChangeStatus ? () => setDeleting(denomination) : undefined}
                      />
                    ))}
                  </RegionGroup>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {editing !== null && (
        <DenominationFormDialog
          brandId={details.brand.id}
          brandName={details.brand.name}
          denomination={editing === 'new' ? undefined : editing}
          defaultRegionId={regionFilter ? Number(regionFilter) : details.regions[0]?.id}
          onClose={() => setEditing(null)}
        />
      )}

      {duplicating !== null && (
        <DenominationFormDialog
          brandId={details.brand.id}
          brandName={details.brand.name}
          duplicateOf={duplicating}
          onClose={() => setDuplicating(null)}
        />
      )}

      {reordering && (
        <DenominationOrderDialog
          brandId={details.brand.id}
          brandName={details.brand.name}
          regionId={reordering.id}
          regionName={reordering.name ?? reordering.code ?? `Region #${reordering.id}`}
          onClose={() => setReordering(null)}
        />
      )}

      {deleting && (
        <DeleteDenominationDialog
          key={deleting.id}
          brandId={details.brand.id}
          denomination={deleting}
          onClose={() => setDeleting(null)}
        />
      )}

      {providersFor && (
        <Dialog open onOpenChange={(open) => !open && setProvidersFor(null)}>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-base">
                Providers — {details.brand.name} {providersFor.name}
              </DialogTitle>
              <DialogDescription className="text-xs">
                The cheapest usable provider supplies each order.
              </DialogDescription>
            </DialogHeader>
            <MappingsPanel
              filter={{ denominationId: providersFor.id }}
              createFor={{
                denominationId: providersFor.id,
                label: `${details.brand.name} · ${providersFor.name}`,
                catalogue: {
                  category: details.brand.category.nameEn,
                  brand: details.brand.name,
                  denomination: providersFor.name,
                },
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {pending && (
        <CatalogueChangeDialog
          key={`${pending.change}:${pending.denominationIds.join(',')}`}
          brandId={details.brand.id}
          request={request}
          onOpenChange={(open) => !open && setPending(null)}
          title={TITLES[pending.change](pending.noun, here)}
          description={DESCRIPTIONS[pending.change](here)}
          confirmLabel={CONFIRM[pending.change]}
          variant={
            pending.change === 'deactivate' || pending.change === 'withdraw'
              ? 'destructive'
              : 'default'
          }
          isPending={setStatus.isPending || setAvailability.isPending}
          countryName={countryName}
          denominationName={denominationName}
          onConfirm={onConfirm}
        />
      )}
    </section>
  );
}

const CHANGE = {
  activate: 'activateDenominations',
  deactivate: 'deactivateDenominations',
  offer: 'offer',
  withdraw: 'withdraw',
} as const;

const TITLES: Record<BulkChange, (noun: string, here: string) => string> = {
  activate: (noun) => `Activate ${noun}?`,
  deactivate: (noun) => `Deactivate ${noun}?`,
  offer: (noun, here) => `Offer ${noun} in ${here}?`,
  withdraw: (noun, here) => `Withdraw ${noun} from ${here}?`,
};

const DESCRIPTIONS: Record<BulkChange, (here: string) => string> = {
  activate: () => 'They become available again in every country where they are offered.',
  deactivate: () =>
    'They are hidden from customers in every country. Existing orders are unaffected.',
  offer: (here) =>
    `Customers in ${here} can see them, and buy them when a provider can supply them.`,
  withdraw: (here) => `Customers in ${here} no longer see them. Other countries are unaffected.`,
};

const CONFIRM: Record<BulkChange, string> = {
  activate: 'Activate',
  deactivate: 'Deactivate',
  offer: 'Offer',
  withdraw: 'Withdraw',
};

function deletionOf(check: CatalogueDeletionCheck | undefined, denominationId: number) {
  return check?.denominations.find((item) => item.denominationId === denominationId);
}

function stateIn(denomination: BrandDenomination, countryId: number) {
  return denomination.countries.find((state) => state.countryId === countryId);
}

function RegionGroup({
  title,
  regionInactive,
  count,
  expanded,
  onToggle,
  columns,
  onReorder,
  children,
}: {
  title: string;
  regionInactive: boolean;
  /** Denominations shown under it with the current filters. */
  count: number;
  expanded: boolean;
  onToggle: () => void;
  columns: number;
  /** Absent without catalogue.manage and all-country scope, or with one denomination. */
  onReorder?: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <TableRow className="bg-muted/40 hover:bg-muted/40">
        <TableCell colSpan={columns} className="py-1.5 font-semibold">
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
              className="hover:text-foreground -ms-1 flex items-center gap-1.5 rounded-sm px-1 py-0.5"
            >
              <ChevronRight
                className={cn(
                  'size-3.5 transition-transform',
                  expanded ? 'rotate-90' : 'rtl:rotate-180',
                )}
                aria-hidden
              />
              {title}
              <span className="text-muted-foreground font-normal tabular-nums">({count})</span>
            </button>
            {regionInactive && <StatusBadge variant="neutral">Region inactive</StatusBadge>}
            {onReorder && (
              <Button
                size="sm"
                variant="ghost"
                className="ms-auto h-7 font-normal"
                onClick={onReorder}
                aria-label={`Reorder ${title} denominations`}
              >
                <ArrowUpDown className="size-3.5" aria-hidden />
                Reorder
              </Button>
            )}
          </span>
        </TableCell>
      </TableRow>
      {expanded && children}
    </>
  );
}

function DenominationRow({
  denomination,
  countryId,
  otherCountries,
  canSeeCost,
  selectable,
  selected,
  onSelect,
  onEdit,
  onChangeStatus,
  onDuplicate,
  onManageProviders,
  deletion,
  onDelete,
}: {
  denomination: BrandDenomination;
  countryId: number;
  otherCountries: CatalogueCountry[];
  canSeeCost: boolean;
  selectable: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  /** Absent for staff who cannot edit denominations. */
  onEdit?: () => void;
  /** Activates or deactivates this one denomination; absent for staff who cannot. */
  onChangeStatus?: () => void;
  /** Absent when there is no other region to copy it to. */
  onDuplicate?: () => void;
  onManageProviders: () => void;
  /** Undefined while the deletion check loads. */
  deletion?: CatalogueDeletionCheck['denominations'][number];
  /** Absent for staff who cannot delete denominations. */
  onDelete?: () => void;
}) {
  const state = stateIn(denomination, countryId);

  return (
    <TableRow data-state={selected ? 'selected' : undefined}>
      {selectable && (
        <TableCell>
          <Checkbox
            aria-label={`Select ${denomination.name}`}
            checked={selected}
            onCheckedChange={(value) => onSelect(value === true)}
          />
        </TableCell>
      )}
      <TableCell className="font-medium">
        {denomination.name}
        {denomination.currencyCode && (
          <span className="text-muted-foreground ms-1 font-normal">
            {denomination.currencyCode}
          </span>
        )}
      </TableCell>
      {/* Within its region; changed with the region's Reorder. */}
      <TableCell className="text-end tabular-nums">{denomination.sortOrder}</TableCell>
      <TableCell>
        <StatusBadge variant={denomination.isActive ? 'success' : 'neutral'}>
          {denomination.isActive ? 'Active' : 'Inactive'}
        </StatusBadge>
      </TableCell>
      <TableCell>
        {state?.offered ? (
          <StatusBadge variant="info">Offered</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">Not offered</StatusBadge>
        )}
      </TableCell>
      <TableCell>{state ? <CustomerState state={state} /> : '—'}</TableCell>
      <TableCell>
        <ProviderCell
          denomination={denomination}
          state={state}
          canSeeCost={canSeeCost}
          onManage={onManageProviders}
        />
      </TableCell>
      <TableCell>
        <OtherCountries denomination={denomination} countries={otherCountries} />
      </TableCell>
      <TableCell>
        <span className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={`Manage providers for ${denomination.name}`}
            title="Providers"
            onClick={onManageProviders}
          >
            <Link2 className="size-3.5" aria-hidden />
          </Button>
          {onEdit && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={`Edit ${denomination.name}`}
                onClick={onEdit}
              >
                <Pencil className="size-3.5" aria-hidden />
              </Button>
              <HistoryButton
                entityType="Denomination"
                entityId={String(denomination.id)}
                name={denomination.name}
              />
            </>
          )}
          {onChangeStatus && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={`${denomination.isActive ? 'Deactivate' : 'Activate'} ${denomination.name}`}
              title={denomination.isActive ? 'Deactivate' : 'Activate'}
              onClick={onChangeStatus}
            >
              {denomination.isActive ? (
                <PowerOff className="size-3.5" aria-hidden />
              ) : (
                <Power className="size-3.5" aria-hidden />
              )}
            </Button>
          )}
          {onDuplicate && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={`Duplicate ${denomination.name} to another region`}
              onClick={onDuplicate}
            >
              <Copy className="size-3.5" aria-hidden />
            </Button>
          )}
          {onDelete && (
            <DeleteDenominationButton
              name={denomination.name}
              deletion={deletion}
              onDelete={onDelete}
            />
          )}
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * Enabled only when the deletion check says nothing uses the denomination; a
 * used one says what uses it (the Admin API refuses it anyway).
 */
function DeleteDenominationButton({
  name,
  deletion,
  onDelete,
}: {
  name: string;
  deletion?: CatalogueDeletionCheck['denominations'][number];
  onDelete: () => void;
}) {
  const reason = !deletion
    ? 'Checking whether anything uses it…'
    : deletion.canDelete
      ? undefined
      : `Can't delete: used by ${describeUsage(deletion.usedBy)}. Deactivate it instead.`;

  return (
    <span title={reason} className="inline-flex">
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive size-7"
        aria-label={reason ? `Delete ${name} — ${reason}` : `Delete ${name}`}
        disabled={!deletion?.canDelete}
        onClick={onDelete}
      >
        <Trash2 className="size-3.5" aria-hidden />
      </Button>
    </span>
  );
}

/** Purchasable, visible-only, or hidden — with the reasons when not purchasable. */
function CustomerState({ state }: { state: DenominationCountryState }) {
  if (state.sellable) return <StatusBadge variant="success">Purchasable</StatusBadge>;

  const reasons = state.visible ? state.sellabilityReasons : state.visibilityReasons;

  return (
    <div className="space-y-0.5">
      <StatusBadge variant={state.visible ? 'warning' : 'neutral'}>
        {state.visible ? 'Visible, cannot buy' : 'Hidden'}
      </StatusBadge>
      {reasons.length > 0 && (
        <p className="text-muted-foreground text-[11px]">{reasons.map(reasonLabel).join(' · ')}</p>
      )}
    </div>
  );
}

function ProviderCell({
  denomination,
  state,
  canSeeCost,
  onManage,
}: {
  denomination: BrandDenomination;
  state: DenominationCountryState | undefined;
  canSeeCost: boolean;
  /** Opens the denomination's provider mappings. */
  onManage: () => void;
}) {
  const chosen = state?.chosenProvider ?? null;
  const unusable = state?.unusableProviders ?? [];
  // Usable here: switched on AND able to supply in this country.
  const usableMappings = denomination.mappings.filter(
    (m) => m.isActive && m.vendorIsActive && !unusable.some((u) => u.vendorId === m.vendorId),
  );
  const reasonTone = chosen
    ? 'border-status-warning-border bg-status-warning-bg/60 text-status-warning'
    : 'border-status-danger-border bg-status-danger-bg/60 text-status-danger';

  return (
    <div className="flex flex-col items-start gap-1.5">
      {/* Status and the mappings action share one row at the same height. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {chosen ? (
          <p>
            {chosen.vendorName ?? `Provider #${chosen.vendorId}`}
            {canSeeCost && chosen.cost !== undefined && (
              <span className="text-muted-foreground ms-1 tabular-nums">
                {formatCurrency(chosen.cost, chosen.costCurrency ?? 'USD')}
              </span>
            )}
          </p>
        ) : (
          <StatusBadge variant="danger" className="h-6">
            {state?.costSource === 'Blocked' ? 'All providers blocked' : 'No provider'}
          </StatusBadge>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onManage}
          className="h-6 gap-1 px-2 text-[11px]"
          aria-label={`Providers for ${denomination.name}`}
        >
          <Link2 className="size-3" aria-hidden />
          {denomination.mappings.length === 0
            ? 'No mappings'
            : `${usableMappings.length} of ${denomination.mappings.length} mappings usable`}
        </Button>
      </div>

      {unusable.length > 0 && (
        // With a working provider the item still sells; this warns that a fallback is broken.
        // Without one, it says why every provider is blocked.
        // The icon has its own column so the heading and every reason start on the same line.
        <div
          className={`grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-0.5 rounded-md border px-2 py-1.5 text-[11px] ${reasonTone}`}
          role="status"
        >
          <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
          <p className="font-semibold">
            {chosen
              ? unusable.length === 1
                ? '1 provider not working'
                : `${unusable.length} providers not working`
              : 'Why no provider can supply'}
          </p>
          <ul className="col-start-2 space-y-0.5">
            {unusable.map((provider) => (
              <li key={provider.vendorId}>
                {provider.vendorName ?? `Provider #${provider.vendorId}`}:{' '}
                {reasonLabel(provider.reason)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {denomination.hasZeroCostMapping && (
        <p className="text-status-warning text-[11px]">Has a zero-cost mapping</p>
      )}
    </div>
  );
}

/**
 * A denomination's state in a country other than the selected one. Each state
 * has its own icon as well as its colour, so it never depends on colour alone.
 */
const ELSEWHERE_STATES = {
  sellable: { label: 'Purchasable', variant: 'success', icon: Check },
  notSellable: { label: 'Offered, not purchasable', variant: 'warning', icon: AlertTriangle },
  notOffered: { label: 'Not offered', variant: 'neutral', icon: Minus },
} as const;

function elsewhereState(state: DenominationCountryState | undefined) {
  if (!state?.offered) return ELSEWHERE_STATES.notOffered;
  return state.sellable ? ELSEWHERE_STATES.sellable : ELSEWHERE_STATES.notSellable;
}

/** Explains the "In other countries" badges, above the table. */
function ElsewhereLegend() {
  return (
    <p className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
      <span>In other countries:</span>
      {Object.values(ELSEWHERE_STATES).map(({ label, variant, icon: Icon }) => (
        <span key={label} className="flex items-center gap-1">
          <StatusBadge variant={variant} dot={false} className="px-1">
            <Icon className="size-3" aria-hidden />
          </StatusBadge>
          {label}
        </span>
      ))}
      <span>— hover a country for the reason.</span>
    </p>
  );
}

function OtherCountries({
  denomination,
  countries,
}: {
  denomination: BrandDenomination;
  countries: CatalogueCountry[];
}) {
  if (countries.length === 0) return <span className="text-muted-foreground">—</span>;

  return (
    <ul className="flex flex-wrap gap-1">
      {countries.map((country) => {
        const state = stateIn(denomination, country.id);
        const code = country.code ?? country.nameEn ?? `#${country.id}`;
        const name = country.nameEn ?? code;
        const { label, variant, icon: Icon } = elsewhereState(state);
        const reasons =
          state?.offered && !state.sellable
            ? (state.visible ? state.sellabilityReasons : state.visibilityReasons).map(reasonLabel)
            : [];
        return (
          <li key={country.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  aria-label={`${code}: ${label.toLowerCase()}`}
                  className="focus-visible:ring-ring rounded-md focus-visible:ring-2 focus-visible:outline-none"
                >
                  <StatusBadge variant={variant} dot={false} className="gap-1 font-mono">
                    <Icon className="size-3" aria-hidden />
                    {code}
                  </StatusBadge>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-64 text-xs">
                <p className="font-medium">
                  {name}: {label}
                </p>
                {reasons.length > 0 && <p>{reasons.join(' · ')}</p>}
              </TooltipContent>
            </Tooltip>
          </li>
        );
      })}
    </ul>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  anyLabel,
  options,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  anyLabel: string;
  options: { value: string; label: string }[];
}) {
  return (
    <Select value={value ?? ANY} onValueChange={(next) => onChange(next === ANY ? null : next)}>
      <SelectTrigger size="sm" className="h-8 w-auto min-w-32 text-xs" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY} className="text-xs">
          {anyLabel}
        </SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} className="text-xs">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
